import {useState, useEffect, useContext, useMemo} from 'react';

import {Grid} from '@mui/material';
import {debounce} from '@mui/material/utils';

import {useInboxPage} from './useInboxPage';
import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {UserContext} from '../../../contexts/UserContext/UserContext';
import {CompanyDepartmentsService} from '../../../features/company/profile/services/CompanyDepartmentsService';
import {CompanyLocationsService} from '../../../features/company/profile/services/CompanyLocationsService';
import {CompanyDepartmentsResponse, CompanyProfile} from '../../../features/company/profile/types';
import ListInboxSections from '../../../features/company/recruiting/inbox/components/list-views/ListInboxSections';
import Filters from '../../../features/company/recruiting/inbox/components/page-filters/Filters';
import Header from '../../../features/company/recruiting/inbox/components/page-header/Header';
import {InboxFilters} from '../../../features/company/recruiting/inbox/types/InboxTypes';
import {MatchStatus} from '../../../types/MatchStatus/MatchStatus';
import {CompanyAdminRoleData} from '../../../types/User';

export default function InboxPage() {
  const {currentUser} = useContext(UserContext);
  const companyId = (currentUser?.role?.data as CompanyAdminRoleData)?.companyId;

  const {
    fetchNextByStatus,
    isLoading,
    error,
    newInbox,
    contacted,
    interview,
    hasMoreByStatus,
    totalCountPerStatus,
    initializeData,
    updateMatchTags,
    initialLoad,
  } = useInboxPage();

  const [selected, setSelected] = useState<string>(MatchStatus.Accepted);
  const [companyLocations, setCompanyLocations] = useState<CompanyProfile['locations']>([]);
  const [companyDepartments, setCompanyDepartments] = useState<CompanyDepartmentsResponse['companyDepartments']>([]);
  const [appliedFilters, setAppliedFilters] = useState<InboxFilters>({
    department: [],
    location: [],
    tags: [],
  });

  const companyLocationsService = useMemo(() => new CompanyLocationsService(), []);
  const companyDepartmentsService = useMemo(() => new CompanyDepartmentsService(), []);

  useEffect(() => {
    const handleScroll = debounce(() => {
      const {scrollTop, clientHeight, scrollHeight} = document.documentElement;

      // Fetch data 300px before reaching the bottom of the page
      if (scrollTop + clientHeight >= scrollHeight - 300 && !isLoading) {
        fetchNextByStatus(selected, appliedFilters);
      }
    }, 300); // Delay fetching the next page by 300ms

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [fetchNextByStatus, isLoading, selected, appliedFilters]);

  useEffect(() => {
    const fetchCompanyLocations = async () => {
      try {
        const response = await companyLocationsService.getCompanyLocations(companyId);
        const companyLocationsData = response.locations;
        setCompanyLocations(companyLocationsData);
      } catch (error) {
        console.error('Failed to fetch company locations:', error);
      }
    };

    const fetchCompanyDepartments = async () => {
      try {
        const response = await companyDepartmentsService.getCompanyDepartments(companyId);
        const companyDepartmentsData = response.data.companyDepartments;
        setCompanyDepartments(companyDepartmentsData);
      } catch (error) {
        console.error('Failed to fetch company departments:', error);
      }
    };

    fetchCompanyLocations();
    fetchCompanyDepartments();
  }, [companyId, companyLocationsService, companyDepartmentsService]);

  const handleFilters = (filters: InboxFilters) => {
    setAppliedFilters((prevState: InboxFilters) => {
      return {...prevState, ...filters};
    });
  };

  const handleToggle = (value: string) => {
    switch (value) {
      case MatchStatus.Accepted:
        setSelected(MatchStatus.Accepted);
        break;
      case MatchStatus.Contacted:
        setSelected(MatchStatus.Contacted);
        break;
      case MatchStatus.Interview:
        setSelected(MatchStatus.Interview);
        break;
      default:
        setSelected(MatchStatus.Accepted);
    }
  };

  const selectedItems = () => {
    switch (selected) {
      case MatchStatus.Accepted:
        return newInbox;
      case MatchStatus.Contacted:
        return contacted;
      case MatchStatus.Interview:
        return interview;
      default:
        return newInbox;
    }
  };

  const clearFilters = () => {
    setAppliedFilters({
      department: [],
      location: [],
      tags: [],
    });

    initializeData();
  };

  const applyFilters = () => {
    initializeData(appliedFilters);
  };

  if (error) {
    return <ErrorMessage />;
  }

  return (
    <Grid
      container
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Header handleToggle={handleToggle} selected={selected} totalCountPerStatus={totalCountPerStatus} />
      <Filters
        companyLocations={companyLocations}
        companyDepartments={companyDepartments}
        handleFilters={handleFilters}
        clearFilters={clearFilters}
        applyFilters={applyFilters}
        appliedFilters={appliedFilters}
      />
      <ListInboxSections
        selected={selected}
        selectedItems={selectedItems()}
        isLoading={isLoading}
        initialLoad={initialLoad}
        hasMore={hasMoreByStatus(selected)}
        companyId={companyId}
        initializeData={initializeData}
        updateMatchTags={updateMatchTags}
      />
    </Grid>
  );
}
